import { TypeIdSchema } from '@unipile/db';
import { Static, Type } from '@sinclair/typebox';
import { TypeCompiler } from '@sinclair/typebox/compiler';

export const TrackingDataSchema = Type.Object({
  account_id: TypeIdSchema('account'),
  label: Type.Optional(Type.String()),
});

const OpenTrackingDataSchema = Type.Intersect([
  TrackingDataSchema,
  /** Epoch milliseconds when the pixel was added, just before sending. Absent from pixels created before it existed. */
  Type.Object({ sent_at: Type.Optional(Type.Number()) }),
]);
const ClickTrackingDataSchema = Type.Intersect([TrackingDataSchema, Type.Object({ url: Type.String() })]);

export const OpenTrackingDataValidator = TypeCompiler.Compile(OpenTrackingDataSchema);
export const ClickTrackingDataValidator = TypeCompiler.Compile(ClickTrackingDataSchema);

export const RequestTrackingSchema = Type.Object({
  ip: Type.String({
    description:
      'IP address that requested the tracking resource. For opens, this is often the address of a mail client image proxy or prefetcher rather than the recipient.',
  }),
  user_agent: Type.String({
    description: 'User agent that requested the tracking resource. Empty when the request carried none.',
  }),
  date: Type.String({ description: 'Date the tracking resource was requested, in ISO 8601 format.' }),
});

export const OpenBotReasonSchema = Type.Union(
  [
    Type.Literal('apple_mpp', {
      description:
        'Apple Mail Privacy Protection preloaded the email images. A real recipient uses Apple Mail, but the open does not prove the email was read.',
    }),
    Type.Literal('security_scanner', {
      description:
        'An email security gateway or link scanner (for example Proofpoint, Mimecast, Barracuda or Microsoft) fetched the pixel while inspecting the email.',
    }),
    Type.Literal('bot_user_agent', {
      description: 'The user agent identifies a crawler, a script or a headless browser rather than a mail client.',
    }),
    Type.Literal('empty_user_agent', {
      description: 'The request carried no user agent, which mail clients always send.',
    }),
    Type.Literal('too_fast', {
      description:
        'The pixel was fetched within seconds of sending, faster than a recipient usually opens an email. Typical of security scanners and image prefetching on delivery.',
    }),
  ],
  { description: 'Signal that marked the open as potentially automated.' },
);

export const OpenTrackingPayloadSchema = Type.Composite([
  Type.Omit(OpenTrackingDataSchema, ['account_id', 'sent_at']),
  RequestTrackingSchema,
  Type.Object({
    object: Type.Literal('OpenTrackingPayload'),
    potentially_bot: Type.Boolean({
      description:
        "`true` when the open was likely triggered by a machine (image prefetch, privacy proxy, security scanner or bot) rather than by the recipient reading the email. Opens loaded through a mailbox provider's image proxy when the recipient displays the email (for example Gmail) are not flagged: the proxy hides the recipient's IP and device, but the open is real. The event is still sent so you can decide whether to count it. Detection is best effort: some automated opens are not flagged and some real opens can be.",
    }),
    bot_reasons: Type.Array(OpenBotReasonSchema, {
      description:
        'Every signal that matched, so several can be listed together. Empty when `potentially_bot` is `false`. Use it to filter selectively, for example to keep Apple Mail Privacy Protection opens but exclude security scanners, or to only exclude opens where several signals agree.',
    }),
  }),
]);

export const ClickTrackingPayloadSchema = Type.Composite([
  Type.Omit(ClickTrackingDataSchema, ['account_id']),
  RequestTrackingSchema,
  Type.Object({
    object: Type.Literal('ClickTrackingPayload'),
  }),
]);

export type OpenBotReason = Static<typeof OpenBotReasonSchema>;
export type OpenTrackingPayload = Static<typeof OpenTrackingPayloadSchema>;
export type ClickTrackingPayload = Static<typeof ClickTrackingPayloadSchema>;
